import { useCallback, useEffect, useState } from "react";
import Pagination from "../../../components/commons/Pagination";
import { useNavigate, useParams } from "react-router-dom";
import UserGroupIcon from "../../../assets/icons/UserGroup.svg";
import Breadcrumb from "../../../components/BreadCrumb";
import RemoveRedEyeOutlined from "@mui/icons-material/RemoveRedEyeOutlined";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import AccessTimeOutlinedIcon from "@mui/icons-material/AccessTimeOutlined";
import { Add, DeleteForever } from "@material-ui/icons";
import DropDown from "../../../components/commons/DropDown";
import HorizontalIcon from "../../../assets/icons/horizontalIcon.png";
import EditIcon from "../../../assets/icons/editUpdateIcon.png";
import DeleteIcon from "../../../assets/icons/menuDelete.png";
import AddEditDriverDetailModal from "./Modal/AddEditDriverDetailModal";
import DeleteDriverDetailModal from "./Modal/DeleteDriverDetailModal";
import {
  ErrorMessage,
  Field,
  FieldArray,
  FormikProvider,
  useFormik,
} from "formik";
import MOButtonV2 from "../../../components/commons/MOButtonV2";
import { connect } from "react-redux";
import mobioApi from "../../../services";
import MOSwitch from "../../../components/commons/MOSwitch";

import { toastMessage } from "../../../helpers/toastMessage";
import ToolTipMessage from "../../../components/commons/ToolTipMessage";
import { validationSchema } from "../../../validations/autoDriverEventValidation";
import { getAirportTerminals } from "../../../data/airportTerminal";

const DRIVER_DETAILS_PER_PAGE = 5;

const mapApiDriverToRow = (driver) => ({
  id: driver.id,
  firstName: driver.firstName || "",
  lastName: driver.lastName || "",
  phoneNumber: driver.contact || driver.phoneNumber || "",
  profileImage: driver.imageUrl || null,
  brand: driver.brandName || "",
  model: driver.carModel || "",
  licensePlateNo: driver.licensePlateNumber || "",
  color: driver.color || "",
});

const Index = ({ token }) => {
  const {
    integrationType,
    integrationId,
    country,
    countryId,
    city,
    cityId,
    airportId,
    airportIATA,
  } = useParams();
  const terminalOptions = getAirportTerminals(airportIATA);

  const [brandNames, setBrandNames] = useState([]);
  const [driverDetailsList, setDriverDetailsList] = useState([]);
  const [anchorEl, setAnchorEl] = useState(null);
  const [addEditModalOpen, setAddEditModalOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [editingDriver, setEditingDriver] = useState(null);
  const [deletingDriver, setDeletingDriver] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalDriverCount, setTotalDriverCount] = useState(0);
  const [listRefreshKey, setListRefreshKey] = useState(0);
  const navigation = useNavigate();
  const uploadImageToAWS = (file) => {
    return new Promise(async (resolve, reject) => {
      try {
        const formData = new FormData();
        formData.append("image", file);
        const api = mobioApi(token);
        const response = await api.upload(formData);
        resolve(response.data.url);
      } catch (error) {
        toastMessage("error", "Error in uploading profile image of driver");
        reject(error);
      }
    });
  };

  const fetchDriverDetailsList = useCallback(
    async (page = 1, limit = DRIVER_DETAILS_PER_PAGE) => {
      try {
        const api = mobioApi(token);
        const response = await api.getAutoEventDriverDetails(
          airportId,
          page,
          limit,
        );
        const list = response?.data?.data || [];
        const pagination = response?.data?.pagination || {};
        const total = pagination.total ?? 0;
        setTotalDriverCount(total);
        return {
          items: list.map(mapApiDriverToRow),
          total,
        };
      } catch (error) {
        console.error("Error fetching driver details:", error);
        setTotalDriverCount(0);
        return { items: [], total: 0 };
      }
    },
    [airportId, token, listRefreshKey],
  );

  const handlePageChange = useCallback((items) => {
    setDriverDetailsList(items);
  }, []);

  const refreshDriverList = (page = currentPage) => {
    setListRefreshKey((key) => key + 1);
    if (page !== currentPage) {
      setCurrentPage(page);
    }
  };

  const handleDriverSaved = () => {
    setCurrentPage(1);
    refreshDriverList(1);
  };

  const handleDeleteDriverDetail = async () => {
    if (!deletingDriver?.id) return;
    try {
      const api = mobioApi(token);
      const response = await api.deleteAutoEventDriverDetail(deletingDriver.id);
      toastMessage("success", response?.data?.msg);
      const isLastItemOnPage = driverDetailsList.length === 1;
      const nextPage =
        isLastItemOnPage && currentPage > 1 ? currentPage - 1 : currentPage;
      setDeleteModalOpen(false);
      setDeletingDriver(null);
      setCurrentPage(nextPage);
      refreshDriverList(nextPage);
    } catch (error) {
      toastMessage(
        "error",
        error?.response?.data?.msg ||
          error?.message ||
          "Failed to delete driver",
      );
    }
  };

  const handleSubmit = async (values, { setSubmitting }) => {
    console.log("values", values);
    if (!totalDriverCount) {
      toastMessage("error", "Please add at least one driver detail");
      setSubmitting(false);
      return;
    }
 
    setSubmitting(true);
    try {
      const payload = {
        // firstName: primaryDriver.firstName,
        // lastName: primaryDriver.lastName,
        // contact: primaryDriver.phone,
        // imageUrl: primaryDriver.profileImage,
        // brandName: primaryDriver.brand,
        // carModel: primaryDriver.model,
        // color: primaryDriver.color,
        // licensePlateNumber: primaryDriver.licensePlateNo,
        rideEventIsActive: values.driverDepartedToAirportEvent.isActive,
        rideMints: values.driverDepartedToAirportEvent.mins,
        rideLng: values.driverDepartedToAirportEvent.longitude || null,
        rideLat: values.driverDepartedToAirportEvent.latitude || null,
        arrivedEventIsActive: values.driverArrivedToAirportEvent.isActive,
        arrivedMints: values.driverArrivedToAirportEvent.mins,
        arrivedLng: values.driverArrivedToAirportEvent.longitude || null,
        arrivedLat: values.driverArrivedToAirportEvent.latitude || null,
        noShowEventIsActive: values.driverNoShowToAirportEvent.isActive,
        noShowMints: values.driverNoShowToAirportEvent.mins,
        noShowLng: values.driverNoShowToAirportEvent.longitude || null,
        noShowLat: values.driverNoShowToAirportEvent.latitude || null,
        airportId: airportId,
        upFrontDriverStatus: values.autoDriverAssignment.isActive,
        upFrontDriverTime: values.autoDriverAssignment.hours.toString(),
        rideSubEvents: (
          values.driverDepartedToAirportEvent.extraCoordinates || []
        ).map((coord) => {
          const item = {
            longitude: coord?.longitude ?? "",
            latitude: coord?.latitude ?? "",
          };
          if (integrationType === "Trip.com") {
            item.terminal = coord?.terminal || "";
          }
          return item;
        }),
        arrivedSubEvents: (
          values.driverArrivedToAirportEvent.extraCoordinates || []
        ).map((coord) => {
          const item = {
            longitude: coord?.longitude ?? "",
            latitude: coord?.latitude ?? "",
          };
          if (integrationType === "Trip.com") {
            item.terminal = coord?.terminal || "";
          }
          return item;
        }),
        noShowSubEvents: [],
      };
  
      const api = mobioApi(token);
      const response = await api.addAutoDriverEvent(payload);
      toastMessage("success", response?.data?.msg);
     
    } catch (error) {
      toastMessage("error", error);
    }
    setSubmitting(false);
  };

  const formik = useFormik({
    initialValues: {
      driverDepartedToAirportEvent: {
        isActive: false,
        mins: 0,
        latitude: 0,
        longitude: 0,
        extraCoordinates: [],
      },
      driverArrivedToAirportEvent: {
        isActive: false,
        mins: 0,
        latitude: 0,
        longitude: 0,
        extraCoordinates: [],
      },
      driverNoShowToAirportEvent: {
        isActive: false,
        mins: 0,
        latitude: 0,
        longitude: 0,
        extraCoordinates: [],
      },
      autoDriverAssignment: {
        hours: 0,
        isActive: false,
      },
    },
    validationSchema: validationSchema(integrationType === "Trip.com"),
    onSubmit: handleSubmit,
  });

  console.log("formik", formik.errors);

  const secondaryPaths = [
    {
      name: country,
      url: `/dashboard/integration/rates/${integrationType}/${integrationId}`,
    },
    {
      name: city,
      url: `/dashboard/integration/rates/${integrationType}/${integrationId}/${country}/${countryId}/city`,
    },
    {
      name: "Airports",
      url: `/dashboard/integration/rates/${integrationType}/${integrationId}/${country}/${countryId}/${city}/${cityId}/city-airport`,
    },

    {
      name: `Manage ${airportIATA}`,
      url: `/dashboard/integration/rates/${integrationType}/${integrationId}/${country}/${countryId}/${city}/${cityId}/airport/${airportIATA}/${airportId}/mange-location`,
    },
    {
      name: "Auto Driver Events",
    },
  ];
  const getAutoDriverEvent = async () => {
    try {
      const api = mobioApi(token);
      const response = await api.getAutoDriverEvent(airportId);
      const carBrandNames = await api.getAllBrands();

      setBrandNames(
        carBrandNames.data.map((carBrandName) => carBrandName.brandName),
      );

      const apiData = response?.data?.data;

      if (apiData) {
        // setDriverDetailsList([
        //   {
        //     id: apiData.id || "driver-api-1",
        //     firstName: apiData.firstName || "",
        //     lastName: apiData.lastName || "",
        //     phone: apiData.contact || "",
        //     profileImage: apiData.imageUrl || null,
        //     brand: apiData.brandName || "",
        //     model: apiData.carModel || "",
        //     color: apiData.color || "",
        //     licensePlateNo: apiData.licensePlateNumber || "",
        //   },
        // ]);
        formik.setValues({
          driverDepartedToAirportEvent: {
            isActive: apiData.rideEventIsActive || false,
            mins: Number(apiData.rideMints) || 0,
            latitude: apiData.rideLat?.toString() || "",
            longitude: apiData.rideLng?.toString() || "",
            extraCoordinates: (apiData.rideSubEvents || []).map((coord) =>
              integrationType === "Trip.com"
                ? { ...coord, terminal: coord?.terminal || "" }
                : coord,
            ),
          },
          driverArrivedToAirportEvent: {
            isActive: apiData.arrivedEventIsActive || false,
            mins: Number(apiData.arrivedMints) || 0,
            latitude: apiData.arrivedLat?.toString() || "",
            longitude: apiData.arrivedLng?.toString() || "",
            extraCoordinates: (apiData.arrivedSubEvents || []).map((coord) =>
              integrationType === "Trip.com"
                ? { ...coord, terminal: coord?.terminal || "" }
                : coord,
            ),
          },
          driverNoShowToAirportEvent: {
            isActive: apiData.noShowEventIsActive || false,
            mins: Number(apiData.noShowMints) || 0,
            latitude: apiData.noShowLat?.toString() || "",
            longitude: apiData.noShowLng?.toString() || "",
            extraCoordinates: apiData.noShowSubEvents,
          },
          autoDriverAssignment: {
            isActive: apiData.upFrontDriverStatus || false,
            hours: Number(apiData.upFrontDriverTime) || 0,
          },
        });
      }
    } catch (error) {
      toastMessage("error", "Failed to load driver data");
      console.error("Error fetching driver data:", error);
    }
  };
  useEffect(() => {
    getAutoDriverEvent();
  }, []);

  useEffect(() => {
    if (!addEditModalOpen) {
      setEditingDriver(null);
    }
  }, [addEditModalOpen]);

  useEffect(() => {
    if (!deleteModalOpen) {
      setDeletingDriver(null);
    }
  }, [deleteModalOpen]);

  const getProfileImagePreview = (profileImage) => {
    if (!profileImage) return null;
    if (typeof profileImage === "string") return profileImage;
    return URL.createObjectURL(profileImage);
  };

  const handleLocation = (type) => {
    let coordinates;

    switch (type) {
      case "rideStart":
        coordinates = {
          lat: formik.values.driverDepartedToAirportEvent.latitude,
          lng: formik.values.driverDepartedToAirportEvent.longitude,
        };
        break;
      case "arrived":
        coordinates = {
          lat: formik.values.driverArrivedToAirportEvent.latitude,
          lng: formik.values.driverArrivedToAirportEvent.longitude,
        };
        break;
      case "noShow":
        coordinates = {
          lat: formik.values.driverNoShowToAirportEvent.latitude,
          lng: formik.values.driverNoShowToAirportEvent.longitude,
        };
        break;
      default:
        coordinates = { lat: "", lng: "" };
    }
    navigation("location", {
      state: {
        type,
        coordinates,
        title: `${type.charAt(0).toUpperCase() + type.slice(1)} Location`,
      },
    });
  };

  return (
    <>
      <div>
        <div
          className="mt-18"
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <div>
            <Breadcrumb
              paths={[
                {
                  name: "Auto Driver Events",
                  url: `/dashboard/integration/rates/${integrationType}/${integrationId}/${country}/${countryId}/${city}/${cityId}/airport/${airportIATA}/${airportId}/mange-location`,
                },
              ]}
            />
            <div className="breadcrumb-description">
              Need this section to set up Auto events for each location
            </div>
            <div className="mt-8 ml-6">
              <Breadcrumb paths={secondaryPaths} secondary={true} />
            </div>
          </div>
        </div>
        {/* Driver Details Section */}
        <div className="bg-white  border-radius-8 mt-24 ">
          <div
            className="flex justify-content-between align-item-center p-28"
            style={{ paddingBottom: 0 }}
          >
            <div className="flex gap-10">
              <div>
                <img src={UserGroupIcon} alt="UserIcon" />
              </div>
              <h1 className="font-14 font-500 mb-0">Driver Details</h1>
            </div>
            {(integrationType === "Trip.com" ||
                driverDetailsList?.length < 1) && (
                <MOButtonV2
                  text="ADD NEW DRIVER DETAIL"
                  textColor="#ffffff"
                  radius={4}
                  backgroundColor="#29AEE6"
                  height={30}
                  fontSize={11}
                  padding={"6.5px 31px"}
                  IconType={() => (
                    <Add style={{ marginRight: 10, fontSize: 16 }} />
                  )}
                  onClick={() => {
                    setEditingDriver(null);
                    setAddEditModalOpen(true);
                  }}
                />
              )}
           
          </div>
          <div className="mt-20 mb-24">
            <div className="campaign-table-section">
              <table className="custom-table-v3">
                <thead>
                  <tr>
                    <th>First Name</th>
                    <th>Last Name</th>
                    <th>Phone</th>
                    <th>Brand</th>
                    <th>Model</th>
                    <th>License Plate</th>
                    <th>Color</th>
                    {/* <th>Profile</th> */}
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {driverDetailsList?.length ? (
                    driverDetailsList.map((row) => (
                      <tr key={row.id}>
                        <td>
                          <div className="flex align-item-center">
                            {row.profileImage ? (
                              <img
                                src={
                                  getProfileImagePreview(row.profileImage) || ""
                                }
                                alt="driver profile"
                                style={{
                                  width: 26,
                                  height: 26,
                                  borderRadius: 100,
                                  objectFit: "cover",
                                }}
                              />
                            ) : (
                              <>
                      
                                <img
                                  src={UserGroupIcon}
                                  alt="driver profile"
                                  style={{
                                    width: 26,
                                    height: 26,
                                    borderRadius: 100,
                                    objectFit: "fill",
                                  }}
                                />
                              </>
                            )}

                            <p className="m-0 ml-8"> {row.firstName}</p>
                          </div>
                        </td>
                        <td>{row.lastName}</td>
                        <td>{row.phoneNumber}</td>
                        <td>{row.brand}</td>
                        <td>{row.model}</td>
                        <td>{row.licensePlateNo}</td>
                        <td>{row.color}</td>

                        <td>
                          <DropDown
                            dropDownOpen={anchorEl}
                            setDropDownOpen={setAnchorEl}
                            uniqueId={row.id}
                            IconRender={() => <img src={HorizontalIcon} />}
                            dropDownShape="horizontal"
                            menu={[
                              {
                                onClick: () => {
                                  setEditingDriver(row);
                                  setAddEditModalOpen(true);
                                },
                                IconRender: () => <img src={EditIcon} />,
                                title: "Edit",
                              },
                              {
                                onClick: () => {
                                  setDeletingDriver(row);
                                  setDeleteModalOpen(true);
                                },
                                IconRender: () => <img src={DeleteIcon} />,
                                title: "Delete",
                              },
                            ]}
                          />
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={9}>
                        <p className="text-align-center">
                          No driver details added yet
                        </p>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
            <div
              style={{
                backgroundColor: "white",
                display: "flex",
                justifyContent: "center",
              }}
            >
              <Pagination
                fetchData={fetchDriverDetailsList}
                itemsPerPage={DRIVER_DETAILS_PER_PAGE}
                onPageChange={handlePageChange}
                currentPage={currentPage}
                onCurrentPageChange={(page) => setCurrentPage(page)}
              />
            </div>
          </div>
        </div>
        {/* Auto Driver Assignment Form */}
        <div className="bg-white auto-driver-spacing-spacing border-radius-8 ">
          <div className="">
            {/* form */}

            <FormikProvider value={formik}>
              <form onSubmit={formik.handleSubmit}>
                {/* Driver Details Table */}

                <div>
                  {/* Driver assign in advance */}
                  <div>
                    {/* header */}
                    <div className="flex gap-10 items-center mt-24">
                      <AccessTimeOutlinedIcon style={{ fontSize: 20 }} />
                      <p className="font-13 font-500 text-color-black">
                        Auto Driver Assignment
                      </p>
                      <ToolTipMessage
                        type="primary"
                        content="Auto-send driver details at your set lead time before pickup (pickup local time). Example: 12h → sent 12 hours before pickup."
                      >
                        <div>
                          <InfoOutlinedIcon
                            style={{
                              color: "#29AEE6",
                              fontSize: 16,
                              cursor: "pointer",
                            }}
                          />
                        </div>
                      </ToolTipMessage>
                    </div>
                    {/* form detail */}
                    <div className="box-shadow-all-3 auto-driver-field-spacing-spacing mt-22">
                      <div className="">
                        {/* Switch Section */}
                        <div>
                          <MOSwitch
                            name="autoDriverAssignment.isActive"
                            isOnStatus={
                              formik.values.autoDriverAssignment.isActive
                            }
                            id={13}
                            setStatus={(switchValue) => {
                              formik.setFieldValue("autoDriverAssignment", {
                                ...formik.values.autoDriverAssignment,
                                isActive: switchValue,
                              });
                            }}
                            isParentControl={false}
                          />
                        </div>
                        {/* Input Field Section */}
                        <div className="mt-16">
                          <div className="input_field">
                            <p className="text-gray font-16 font-500 mb-12">
                              Lead Time (Hours)
                            </p>
                            <Field
                              name="autoDriverAssignment.hours"
                              type="number"
                              min={0}
                              placeholder="Enter time in hours"
                              className="input-field-styles"
                              // style={{
                              //   borderColor: "#29AEE6",
                              //   borderWidth: "1.8px",
                              //   borderRadius: 5,
                              //   position: "relative",
                              // }}
                              value={
                                formik.values.autoDriverAssignment.hours ?? ""
                              }
                              onChange={(e) => {
                                const value =
                                  e.target.value === ""
                                    ? null
                                    : Number(e.target.value);
                                formik.setFieldValue(
                                  "autoDriverAssignment.hours",
                                  value,
                                );
                              }}
                            />
                          </div>

                          <ErrorMessage name="autoDriverAssignment.hours">
                            {(msg) => (
                              <div style={{ color: "red", margin: "5px" }}>
                                {msg}
                              </div>
                            )}
                          </ErrorMessage>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Cron job section: */}
                  <div className="flex gap-10 align-items-center mt-24">
                    <AccessTimeOutlinedIcon style={{ fontSize: 20 }} />
                    <p className="font-13 font-500 text-color-black">
                      Setup Driver Events
                    </p>
                    <ToolTipMessage
                      type="primary"
                      content="The events will be triggered based on the flight landing time if a flight number is provided.
                                            If no flight number is available, they will be triggered according to the pickup time."
                    >
                      <div className="">
                        <div>
                          <InfoOutlinedIcon
                            style={{
                              color: "#29AEE6",
                              fontSize: 16,
                              cursor: "pointer",
                            }}
                          />
                        </div>
                      </div>
                    </ToolTipMessage>
                  </div>

                  {/* Ride Section */}
                  <div className="box-shadow-all-3 auto-driver-field-spacing-spacing mt-22">
                    <div className="flex align-items-center justify-content-between">
                      <MOSwitch
                        name="driverDepartedToAirportEvent.isActive"
                        isOnStatus={
                          formik.values.driverDepartedToAirportEvent.isActive
                        }
                        id={1}
                        setStatus={(switchValue) => {
                          formik.setFieldValue(`driverDepartedToAirportEvent`, {
                            ...formik.values.driverDepartedToAirportEvent,
                            isActive: switchValue,
                          });
                        }}
                        isParentControl={false}
                      />
                      <div>
                        <RemoveRedEyeOutlined
                          style={{ cursor: "pointer", color: "#2CA9DD" }}
                          onClick={() => handleLocation("rideStart")}
                        />
                      </div>
                    </div>
                    <div className="flex gap-24 mt-16">
                      <div className="flex-1">
                        <div className="input_field">
                          <p className="text-primary font-16 font-500 mb-12">
                            Start Ride:
                          </p>
                          <Field
                            name="driverDepartedToAirportEvent.mins"
                            type="number"
                            min={0}
                            placeholder="10 Minutes"
                            className="input-field-styles"
                            style={{
                              borderColor: "#29AEE6",
                              borderWidth: "1.8px",
                              borderRadius: 5,
                              position: "relative",
                            }}
                            value={
                              formik.values.driverDepartedToAirportEvent.mins ??
                              ""
                            }
                            onChange={(e) => {
                              const value =
                                e.target.value === ""
                                  ? null
                                  : Number(e.target.value);
                              formik.setFieldValue(
                                "driverDepartedToAirportEvent.mins",
                                value,
                              );
                            }}
                          />
                        </div>
                        <ErrorMessage name="driverDepartedToAirportEvent.mins">
                          {(msg) => (
                            <div style={{ color: "red", margin: "5px" }}>
                              {msg}
                            </div>
                          )}
                        </ErrorMessage>
                      </div>
                      <div className="flex-1">
                        <div className="input_field">
                          <p
                            className="text-gray font-16 font-500 mb-12"
                            style={{
                              color: "#428702",
                            }}
                          >
                            Longitude
                          </p>
                          <Field
                            type="number"
                            name="driverDepartedToAirportEvent.longitude"
                            placeholder="435:378:4567"
                            className="input-field-styles"
                            style={{
                              borderColor: "#428702",
                            }}
                            value={
                              formik.values.driverDepartedToAirportEvent
                                .longitude
                            }
                            onChange={(e) => {
                              formik.setFieldValue(
                                "driverDepartedToAirportEvent.longitude",
                                e.target.value,
                              );
                            }}
                          />
                        </div>
                        <ErrorMessage name="driverDepartedToAirportEvent.longitude">
                          {(msg) => (
                            <div style={{ color: "red", margin: "5px" }}>
                              {msg}
                            </div>
                          )}
                        </ErrorMessage>
                      </div>
                      <div className="flex-1">
                        <div className="input_field">
                          <p
                            className="text-gray font-16 font-500 mb-12"
                            style={{
                              color: "#428702",
                            }}
                          >
                            Latitude
                          </p>
                          <Field
                            type="number"
                            name="driverDepartedToAirportEvent.latitude"
                            placeholder="435:378:4567"
                            className="input-field-styles"
                            style={{
                              borderColor: "#428702",
                            }}
                            value={
                              formik.values.driverDepartedToAirportEvent
                                .latitude
                            }
                            onChange={(e) => {
                              formik.setFieldValue(
                                "driverDepartedToAirportEvent.latitude",
                                e.target.value,
                              );
                            }}
                          />
                          <ErrorMessage name="driverDepartedToAirportEvent.latitude">
                            {(msg) => (
                              <div style={{ color: "red", margin: "5px" }}>
                                {msg}
                              </div>
                            )}
                          </ErrorMessage>
                        </div>
                      </div>
                    </div>

                    {/* adding more coordinates */}
                    <div className="mt-20">
                      <div>
                        {/* dynamic */}

                        <FieldArray name="driverDepartedToAirportEvent.extraCoordinates">
                          {({ push, remove }) => (
                            <div className="mt-20">
                              <MOButtonV2
                                type="button"
                                text={integrationType === "Trip.com" ? "+ Add Terminal Coordinate" : "+ Add Coordinate"}
                                textColor="#ffffff"
                                radius={4}
                                backgroundColor="#29AEE6"
                                height={30}
                                fontSize={12}
                                padding={"8.5px 12px"}
                                disabled={formik.isSubmitting}
                                loading={formik.isSubmitting}
                                onClick={() =>
                                  push(
                                    integrationType === "Trip.com"
                                      ? {
                                          longitude: "",
                                          latitude: "",
                                          terminal: "",
                                        }
                                      : {
                                          longitude: "",
                                          latitude: "",
                                        },
                                  )
                                }
                              />
                              <p className="font-12 text-gray mt-4 ">
                                {integrationType === "Trip.com"
                                  ? "Click on button if you want add more coordinates and a terminal that you want send after "
                                  : "Click on button if you want add more coordinates that you want send after "}
                                <strong>Start Ride </strong>to pickup event
                              </p>

                              {formik?.values?.driverDepartedToAirportEvent?.extraCoordinates?.map(
                                (coord, index) => (
                                  <div
                                    key={index}
                                    className="flex gap-24 mt-16 align-item-end"
                                  >
                                    {/* Longitude */}
                                    <div className="flex-1">
                                      <div className="input_field">
                                        <p className="text-gray font-16 font-500 mb-12">
                                          Longitude
                                        </p>
                                        <Field
                                          name={`driverDepartedToAirportEvent.extraCoordinates[${index}].longitude`}
                                          type="number"
                                          className="input-field-styles"
                                        />
                                      </div>
                                      <ErrorMessage
                                        name={`driverDepartedToAirportEvent.extraCoordinates[${index}].longitude`}
                                        component="div"
                                        className="text-danger font-12"
                                      />
                                    </div>

                                    {/* Latitude */}
                                    <div className="flex-1">
                                      <div className="input_field">
                                        <p className="text-gray font-16 font-500 mb-12">
                                          Latitude
                                        </p>
                                        <Field
                                          name={`driverDepartedToAirportEvent.extraCoordinates[${index}].latitude`}
                                          type="number"
                                          className="input-field-styles"
                                        />
                                      </div>
                                      <ErrorMessage
                                        name={`driverDepartedToAirportEvent.extraCoordinates[${index}].latitude`}
                                        component="div"
                                        className="text-danger font-12"
                                      />
                                    </div>

                                    {integrationType === "Trip.com" && (
                                      <div className="flex-1">
                                        <div className="input_field">
                                          <p className="text-gray font-16 font-500 mb-12">
                                            Terminal
                                          </p>
                                          <Field
                                            as="select"
                                            name={`driverDepartedToAirportEvent.extraCoordinates[${index}].terminal`}
                                            className="input-field-styles"
                                          >
                                            <option value="">
                                              Select terminal
                                            </option>
                                            {terminalOptions.map((terminal) => (
                                              <option
                                                key={terminal}
                                                value={terminal}
                                              >
                                                {terminal}
                                              </option>
                                            ))}
                                            {coord?.terminal &&
                                              !terminalOptions.includes(
                                                coord.terminal,
                                              ) && (
                                                <option value={coord.terminal}>
                                                  {coord.terminal}
                                                </option>
                                              )}
                                          </Field>
                                        </div>
                                        <ErrorMessage
                                          name={`driverDepartedToAirportEvent.extraCoordinates[${index}].terminal`}
                                          component="div"
                                          className="text-danger font-12"
                                        />
                                      </div>
                                    )}

                                    {/* Delete Button */}
                                    <DeleteForever
                                      className="text-danger cursor-pointer"
                                      onClick={() => remove(index)}
                                    />
                                  </div>
                                ),
                              )}
                            </div>
                          )}
                        </FieldArray>
                      </div>
                    </div>
                  </div>

                  {/* Arrived */}

                  <div className="box-shadow-all-3 auto-driver-field-spacing-spacing mt-22">
                    <div className="flex align-items-center justify-content-between">
                      <MOSwitch
                        isOnStatus={
                          formik.values.driverArrivedToAirportEvent.isActive
                        }
                        id={2}
                        setStatus={(switchValue) => {
                          formik.setFieldValue(`driverArrivedToAirportEvent`, {
                            ...formik.values.driverArrivedToAirportEvent,
                            isActive: switchValue,
                          });
                        }}
                        isParentControl={false}
                      />
                      <div>
                        <RemoveRedEyeOutlined
                          style={{ cursor: "pointer", color: "#2CA9DD" }}
                          onClick={() => handleLocation("arrived")}
                        />
                      </div>
                    </div>
                    <div className="flex gap-24 mt-16">
                      <div className="flex-1">
                        <div className="input_field">
                          <p className="text-success font-16 font-500 mb-12">
                            Arrived
                          </p>
                          <Field
                            style={{
                              borderColor: "#428702",
                              borderWidth: "1.8px",
                              borderRadius: 5,
                            }}
                            type="number"
                            min={0}
                            name="driverArrivedToAirportEvent.mins"
                            className="input-field-styles"
                            placeholder="15 Minutes"
                            value={
                              formik.values.driverArrivedToAirportEvent.mins ??
                              ""
                            }
                            onChange={(e) => {
                              const value =
                                e.target.value === ""
                                  ? null
                                  : Number(e.target.value);
                              formik.setFieldValue(
                                "driverArrivedToAirportEvent.mins",
                                value,
                              );
                            }}
                          />
                        </div>
                        <ErrorMessage name="driverArrivedToAirportEvent.mins">
                          {(msg) => (
                            <div style={{ color: "red", margin: "5px" }}>
                              {msg}
                            </div>
                          )}
                        </ErrorMessage>
                      </div>
                      <div className="flex-1">
                        <div className="input_field ">
                          <p className="text-gray font-16 font-500 mb-12">
                            Longitude
                          </p>
                          <Field
                            type="number"
                            name="driverArrivedToAirportEvent.longitude"
                            className="input-field-styles"
                            placeholder="435:378:4567"
                            value={
                              formik.values.driverArrivedToAirportEvent
                                .longitude
                            }
                            onChange={(e) => {
                              formik.setFieldValue(
                                "driverArrivedToAirportEvent.longitude",
                                e.target.value,
                              );
                            }}
                          />
                          <ErrorMessage name="driverArrivedToAirportEvent.longitude">
                            {(msg) => (
                              <div style={{ color: "red", margin: "5px" }}>
                                {msg}
                              </div>
                            )}
                          </ErrorMessage>
                        </div>
                      </div>
                      <div className="flex-1">
                        <div className="input_field ">
                          <p className="text-gray font-16 font-500 mb-12">
                            Latitude
                          </p>
                          <Field
                            type="number"
                            name="driverArrivedToAirportEvent.latitude"
                            placeholder="435:378:4567"
                            className="input-field-styles"
                            value={
                              formik.values.driverArrivedToAirportEvent.latitude
                            }
                            onChange={(e) => {
                              formik.setFieldValue(
                                "driverArrivedToAirportEvent.latitude",
                                e.target.value,
                              );
                            }}
                          />
                          <ErrorMessage name="driverArrivedToAirportEvent.latitude">
                            {(msg) => (
                              <div style={{ color: "red", margin: "5px" }}>
                                {msg}
                              </div>
                            )}
                          </ErrorMessage>
                        </div>
                      </div>
                    </div>

                    {/* adding more coordinates */}
                    <div className="mt-20">
                      <div>
                        {/* dynamic */}

                        <FieldArray name="driverArrivedToAirportEvent.extraCoordinates">
                          {({ push, remove }) => (
                            <div className="mt-20">
                              <MOButtonV2
                                type="button"
                                text={integrationType === "Trip.com" ? "+ Add Terminal Coordinate" : "+ Add Coordinate"}
                                textColor="#ffffff"
                                radius={4}
                                backgroundColor="#29AEE6"
                                height={30}
                                fontSize={12}
                                padding={"8.5px 12px"}
                                disabled={formik.isSubmitting}
                                loading={formik.isSubmitting}
                                onClick={() =>
                                  push(
                                    integrationType === "Trip.com"
                                      ? {
                                          longitude: "",
                                          latitude: "",
                                          terminal: "",
                                        }
                                      : {
                                          longitude: "",
                                          latitude: "",
                                        },
                                  )
                                }
                              />
                              <p className="font-12 text-gray mt-4 ">
                                {integrationType === "Trip.com"
                                  ? "Click on button if you want add more coordinates and a terminal that you want send after "
                                  : "Click on button if you want add more coordinates that you want send after "}
                                <strong>Arrived </strong>to pickup event
                              </p>

                              {formik?.values?.driverArrivedToAirportEvent?.extraCoordinates?.map(
                                (coord, index) => (
                                  <div
                                    key={index}
                                    className="flex gap-24 mt-16 align-item-end"
                                  >
                                    {/* Longitude */}
                                    <div className="flex-1">
                                      <div className="input_field">
                                        <p className="text-gray font-16 font-500 mb-12">
                                          Longitude
                                        </p>
                                        <Field
                                          name={`driverArrivedToAirportEvent.extraCoordinates[${index}].longitude`}
                                          type="number"
                                          className="input-field-styles"
                                        />
                                      </div>
                                      <ErrorMessage
                                        name={`driverArrivedToAirportEvent.extraCoordinates[${index}].longitude`}
                                        component="div"
                                        className="text-danger font-12"
                                      />
                                    </div>

                                    {/* Latitude */}
                                    <div className="flex-1">
                                      <div className="input_field">
                                        <p className="text-gray font-16 font-500 mb-12">
                                          Latitude
                                        </p>
                                        <Field
                                          name={`driverArrivedToAirportEvent.extraCoordinates[${index}].latitude`}
                                          type="number"
                                          className="input-field-styles"
                                        />
                                      </div>
                                      <ErrorMessage
                                        name={`driverArrivedToAirportEvent.extraCoordinates[${index}].latitude`}
                                        component="div"
                                        className="text-danger font-12"
                                      />
                                    </div>

                                    {integrationType === "Trip.com" && (
                                      <div className="flex-1">
                                        <div className="input_field">
                                          <p className="text-gray font-16 font-500 mb-12">
                                            Terminal
                                          </p>
                                          <Field
                                            as="select"
                                            name={`driverArrivedToAirportEvent.extraCoordinates[${index}].terminal`}
                                            className="input-field-styles"
                                          >
                                            <option value="">
                                              Select terminal
                                            </option>
                                            {terminalOptions.map((terminal) => (
                                              <option
                                                key={terminal}
                                                value={terminal}
                                              >
                                                {terminal}
                                              </option>
                                            ))}
                                            {coord?.terminal &&
                                              !terminalOptions.includes(
                                                coord.terminal,
                                              ) && (
                                                <option value={coord.terminal}>
                                                  {coord.terminal}
                                                </option>
                                              )}
                                          </Field>
                                        </div>
                                        <ErrorMessage
                                          name={`driverArrivedToAirportEvent.extraCoordinates[${index}].terminal`}
                                          component="div"
                                          className="text-danger font-12"
                                        />
                                      </div>
                                    )}

                                    {/* Delete Button */}
                                    <DeleteForever
                                      className="text-danger cursor-pointer"
                                      onClick={() => remove(index)}
                                    />
                                  </div>
                                ),
                              )}
                            </div>
                          )}
                        </FieldArray>
                      </div>
                    </div>
                  </div>

                  {/* No Show: */}
                  <div className="box-shadow-all-3 auto-driver-field-spacing-spacing mt-22">
                    <div className="flex align-items-center justify-content-between">
                      <MOSwitch
                        isOnStatus={
                          formik.values.driverNoShowToAirportEvent.isActive
                        }
                        id={3}
                        setStatus={(switchValue) => {
                          formik.setFieldValue(`driverNoShowToAirportEvent`, {
                            ...formik.values.driverNoShowToAirportEvent,
                            isActive: switchValue,
                          });
                        }}
                        isParentControl={false}
                      />
                      <div>
                        <RemoveRedEyeOutlined
                          style={{ cursor: "pointer", color: "#2CA9DD" }}
                          onClick={() => handleLocation("noShow")}
                        />
                      </div>
                    </div>
                    <div className="flex  gap-24 mt-16">
                      <div className="flex-1">
                        <div className="input_field">
                          <p
                            className="text-gray font-16 font-500  mb-12"
                            style={{
                              color: "#FC0808",
                            }}
                          >
                            No Show:
                          </p>
                          <Field
                            name="driverNoShowToAirportEvent.mins"
                            type="number"
                            min={0}
                            className="input-field-styles"
                            placeholder="10 Minutes"
                            style={{
                              borderColor: "#FC0808",
                              borderWidth: "1.5px",
                              borderRadius: 5,
                            }}
                            value={
                              formik.values.driverNoShowToAirportEvent.mins ??
                              ""
                            }
                            onChange={(e) => {
                              let value =
                                e.target.value === ""
                                  ? null
                                  : Number(e.target.value);
                              formik.setFieldValue(
                                "driverNoShowToAirportEvent.mins",
                                value,
                              );
                            }}
                          />
                        </div>
                        <ErrorMessage name="driverNoShowToAirportEvent.mins">
                          {(msg) => (
                            <div style={{ color: "red", margin: "5px" }}>
                              {msg}
                            </div>
                          )}
                        </ErrorMessage>
                      </div>
                      <div className="flex-1">
                        <div className="input_field ">
                          <p className="text-gray font-16 font-500 mb-12">
                            Longitude
                          </p>
                          <Field
                            type="number"
                            name="driverNoShowToAirportEvent.longitude"
                            placeholder="435:378:4567"
                            className="input-field-styles"
                            value={
                              formik.values.driverNoShowToAirportEvent.longitude
                            }
                            onChange={(e) => {
                              formik.setFieldValue(
                                "driverNoShowToAirportEvent.longitude",
                                e.target.value,
                              );
                            }}
                          />
                          <ErrorMessage name="driverNoShowToAirportEvent.longitude">
                            {(msg) => (
                              <div style={{ color: "red", margin: "5px" }}>
                                {msg}
                              </div>
                            )}
                          </ErrorMessage>
                        </div>
                      </div>
                      <div className="flex-1">
                        <div className="input_field ">
                          <p className="text-gray font-16 font-500 mb-12">
                            Latitude
                          </p>
                          <Field
                            type="number"
                            name="driverNoShowToAirportEvent.latitude"
                            placeholder="435:378:4567"
                            className="input-field-styles"
                            value={
                              formik.values.driverNoShowToAirportEvent.latitude
                            }
                            onChange={(e) => {
                              formik.setFieldValue(
                                "driverNoShowToAirportEvent.latitude",
                                e.target.value,
                              );
                            }}
                          />
                          <ErrorMessage name="driverNoShowToAirportEvent.latitude">
                            {(msg) => (
                              <div style={{ color: "red", margin: "5px" }}>
                                {msg}
                              </div>
                            )}
                          </ErrorMessage>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* save and cancel button */}
                  <div className="Sm-screen flex align-items-center justify-content-end justify-content-md-start mt-24">
                    <div>
                      <MOButtonV2
                        type="submit"
                        text="SAVE AUTO DRIVER EVENTS"
                        textColor="#ffffff"
                        radius={4}
                        backgroundColor="#29AEE6"
                        height={40}
                        fontSize={11}
                        padding={"8.5px 63px"}
                        disabled={formik.isSubmitting}
                        loading={formik.isSubmitting}
                      />
                    </div>
                  </div>
                </div>
              </form>
            </FormikProvider>
          </div>
        </div>
      </div>
      {addEditModalOpen && (
        <AddEditDriverDetailModal
          token={token}
          airportId={airportId}
          modalOpen={addEditModalOpen}
          setModalOpen={setAddEditModalOpen}
          singleData={editingDriver}
          onSuccess={handleDriverSaved}
          brandNames={brandNames}
        />
      )}

      {deleteModalOpen && (
        <DeleteDriverDetailModal
          modalOpen={deleteModalOpen}
          setModalOpen={setDeleteModalOpen}
          deleteAPICall={handleDeleteDriverDetail}
        />
      )}
    </>
  );
};
const mapState = (state) => ({
  token: state.userInfo.token,
});

const mapDispatch = {};

export default connect(mapState, mapDispatch)(Index);
